import { Moment } from 'moment'
import * as R from 'ramda'

import DateFormat from '../constants/dateFormat'
import moment from '../momentLocale'
import { Meridian, Nil, StringWithMeridian } from '../types'
import { matchAllConditions } from './index'

// This is used to send API request and as a helper to validate dates
// As it's not used to display dates, we must not localize this
export function serializeDate(date?: string | Nil) {
  return date ? moment(date).format('YYYY-MM-DD') : null
}

const warnExternalMoment = () =>
  // eslint-disable-next-line no-console
  console.warn(
    'You should be using the moment instance from ui-components to get the right locales formats',
  )

export const getHourRange = (momentOne: Moment, momentTwo: Moment) => {
  const haveSameMeridians = momentOne.format('a') === momentTwo.format('a')

  // @ts-ignore
  // eslint-disable-next-line no-underscore-dangle
  const momentOneLongDateFormat = momentOne.localeData()._longDateFormat
  // @ts-ignore
  // eslint-disable-next-line no-underscore-dangle
  const momentTwoLongDateFormat = momentTwo.localeData()._longDateFormat

  const conditions = [
    R.has(DateFormat.ONLY_MERIDIAN, momentOneLongDateFormat),
    R.has(DateFormat.FULL_TIME_WITHOUT_MERIDIAN, momentOneLongDateFormat),
    R.has(DateFormat.FULL_TIME_WITH_MERIDIAN, momentOneLongDateFormat),
    R.has(DateFormat.ONLY_MERIDIAN, momentTwoLongDateFormat),
    R.has(DateFormat.FULL_TIME_WITHOUT_MERIDIAN, momentTwoLongDateFormat),
    R.has(DateFormat.FULL_TIME_WITH_MERIDIAN, momentTwoLongDateFormat),
  ]

  if (matchAllConditions(conditions)) {
    const firstMoment = momentOne.format(
      haveSameMeridians
        ? DateFormat.FULL_TIME_WITHOUT_MERIDIAN
        : DateFormat.FULL_TIME_WITH_MERIDIAN,
    )
    const secondMoment = momentTwo.format(DateFormat.FULL_TIME_WITH_MERIDIAN)

    return `${firstMoment}-${secondMoment}`
  }

  warnExternalMoment()
  // fallback when someone is sending an external moment instance without custom properties
  return `${momentOne.format(
    `hh:mm${haveSameMeridians ? '' : 'a'}`,
  )}-${momentTwo.format('hh:mma')}`
}

export function formatDateWithHours(
  dateString: string | Nil,
  withPipe?: boolean,
) {
  if (R.isNil(dateString)) {
    return ''
  }

  // fallback when we're sending a moment instance instead of a date string
  if (typeof dateString === 'object') {
    warnExternalMoment()
    return moment(dateString).format(
      `${moment.localeData().longDateFormat('L')} ${
        withPipe ? '|' : ''
      } hh:mma`,
    )
  }

  return moment(dateString).format(
    withPipe ? DateFormat.FULL_DATE_TIME_WITH_PIPE : DateFormat.FULL_DATE_TIME,
  )
}

export const formatDate = (
  dateString: string | Nil,
  utc?: boolean,
  format?: string | DateFormat,
) =>
  dateString
    ? (utc ? moment(dateString).utc() : moment(dateString)).format(
      format || 'L',
    )
    : ''

export const formatTime = (date: string) =>
  moment(date).format(DateFormat.FULL_TIME_WITH_MERIDIAN)

export const isAm = (meridian: Meridian) => meridian.toLowerCase() === 'am'

export const isPm = (meridian: Meridian) => meridian.toLowerCase() === 'pm'

export const TimeMeridianRegexAM = /\b((1[0-2]|0?[0-9]):([0-5][0-9])([Aa][Mm]))/
export const TimeMeridianRegexPM = /\b((1[0-2]|0?[1-9]):([0-5][0-9])([Pp][Mm]))/
export const OnlyMeridianRegex = /([AaPp][Mm])/

export const getHourAndMinutesFromMeridian = (
  meridianTime: StringWithMeridian | string,
) => {
  const currentMeridian = meridianTime.slice(-2).toLowerCase() as Meridian
  const [normalizedMeridianTime] = isAm(currentMeridian)
    ? TimeMeridianRegexAM.exec(meridianTime) || []
    : TimeMeridianRegexPM.exec(meridianTime) || []

  if (!normalizedMeridianTime) {
    return undefined
  }

  const [hour, minutes] = normalizedMeridianTime
    .replace(OnlyMeridianRegex, '')
    .split(':')

  const h = Number(hour)
  const m = Number(minutes)

  if (!hour || !minutes || Number.isNaN(h) || Number.isNaN(m)) {
    return undefined
  }

  return isAm(currentMeridian) ? { h, m } : { h: h === 12 ? 0 : h + 12, m }
}

export const convertMeridianTimeTo24 = (
  meridianTime: StringWithMeridian | string,
) => {
  const hourMinute = getHourAndMinutesFromMeridian(meridianTime)
  return hourMinute && formatTime(moment().set(hourMinute).toISOString())
}
